import arcChildIcon from "@assets/arc-child.svg";
import arcEndIcon from "@assets/arc-end.svg";
import arcMidIcon from "@assets/arc-mid.svg";
import ExpandCircleIconDark from "@assets/expand-down-circle-dark.svg";
import ExpandCircleIcon from "@assets/expand-down-circle.svg";
import noLowerLevelIcon from "@assets/no-lower-level.svg";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import MUIBookmarkIcon from "@mui/icons-material/BookmarkTwoTone";
import AnnotationIcon from "@mui/icons-material/StickyNote2TwoTone";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import {
  TreeItem2Content,
  TreeItem2IconContainer,
  TreeItem2Label,
  TreeItem2Root,
} from "@mui/x-tree-view/TreeItem2";
import { TreeItem2Provider } from "@mui/x-tree-view/TreeItem2Provider";
import { useTreeItem2 } from "@mui/x-tree-view/useTreeItem2";
import clsx from "clsx";
import React, { memo } from "react";
import { BookmarkIcon } from "../bookmarks/BookmarkIcon";
import { FlexRowContainer } from "../layout";
import { SearchResult } from "../SearchResult";

export const CustomContent = styled(TreeItem2Content)<{
  menuType?: string;
  annotations?: number;
}>`
  user-select: none;
  border-radius: 0;
  opacity: 1;
  min-height: 2.5rem;
  background: ${({ theme }) => theme.colours.accent.light._10};
  ${({ theme }) => theme.customTypography.book.label._1};
  color: ${({ theme }) => theme.colours.accent.dark._10};
  font-weight: 500;

  box-shadow:
    0 0 0 0.5px rgba(252, 251, 248, 1) inset,
    0 -17px 10px -1px rgba(244, 243, 237, 1) inset;
  background-color: rgba(250, 249, 244, 1);
  & ul {
    padding-left: 0;
    list-style: none;
    margin-left: 0;
  }
  &.searchResult {
    position: sticky;
    &.nesting-1 {
      top: 40px;
      z-index: 9;
    }
    &.nesting-2 {
      top: 80px;
      z-index: 8;
    }
    &.nesting-3 {
      top: 120px;
      z-index: 7;
    }
    &.nesting-4 {
      top: 160px;
      z-index: 6;
    }
    &.nesting-5 {
      top: 200px;
      z-index: 5;
    }
    &:not(.chapter):hover {
      background-color: ${({ theme }) => theme.colours.accent.light._10};
    }
  }

  &.chapter,
  &.Mui-selected.chapter {
    color: ${({ theme }) => theme.colours.accent.light._20};
    background-color: ${({ theme }) => theme.colours.accent.dark._10};
    box-shadow:
      0 0 2px 1px rgba(78, 75, 66, 1) inset,
      0 0 0 0.25px rgba(186, 183, 168, 1) inset;
    ${({ theme }) => theme.customTypography.jumbo._5};
    font-weight: 500;
    &.searchResult {
      top: 0;
      z-index: 10;
    }
  }

  &.Mui-selected.chapter {
    border: 0;
    color: ${({ theme }) => theme.colours.accent.light._20};
    background-color: ${({ theme }) => theme.colours.accent.mid.dark15};
    font-weight: 700;
    border-right: 2px solid ${({ theme }) => theme.colours.accent.dark._40};
  }

  &.section,
  &.Mui-selected.section {
    ${({ theme }) => theme.customTypography.overline._1};
    color: ${({ theme }) => theme.colours.accent.light._10};
    font-weight: 500;
    background-color: ${({ theme }) => theme.colours.accent.dark._30};
    box-shadow:
      0 0 2px 1px rgba(45, 43, 37, 1) inset,
      0 0 0 0.25px rgba(186, 183, 168, 1) inset;
  }

  &.Mui-selected.section {
    border: 0;
    border-right: 2px solid ${({ theme }) => theme.colours.accent.light._60};
    background-color: ${({ theme }) => theme.colours.accent.dark._40};
    font-weight: 700;
  }

  &.subsection {
    background: ${({ theme }) => theme.colours.accent.light._10};
  }

  &.Mui-selected {
    border: 1px solid ${({ theme }) => theme.colours.accent.mid.mid};
    border-right: 2px solid ${({ theme }) => theme.colours.accent.dark._30};
    color: ${({ theme }) => theme.colours.accent.dark._30};
    background-color: rgba(253, 253, 251, 1);
    font-weight: 700;
    box-shadow:
      0 0 4px 2px rgba(252, 251, 248, 1) inset,
      0 0 0 0.5px rgba(255, 255, 255, 1) inset,
      0 -6px 12px -2px rgba(255, 255, 255, 1) inset,
      0 0 12px -6px rgba(237, 236, 229, 1);
  }
`;

const FlatChildList = styled.ul`
  padding-left: 0;
  margin: 0;
  list-style: none;

  & > li {
    margin-left: 0;
    padding-left: 0;
  }
`;

const SubMenuList = styled.ul<{ padMulti?: boolean }>`
  padding-left: 0;
  margin: 0;
  margin-left: 0;
  list-style: none;
  ${({ padMulti }) =>
    padMulti
      ? `& > li > div:not(.MuiCollapse-root) {
    padding-left: 2rem;
  }
  & > li > div.MuiCollapse-root {
    padding: 8px 0;
  }`
      : ` & > li > div {
     padding-left: 2rem;
  }`}
`;

const SectionBookmarkChip = ({ colours }: { colours: string[] }) => {
  const showPlus = colours.length > 3;
  const iconsToShow = colours.slice(0, 3);

  return (
    <FlexRowContainer
      css={({ colours: themeColours }) => css`
        align-items: center;
        background-color: ${themeColours.primary.light._20};
        box-shadow:
          1px 2px 4px rgba(32, 42, 91, 0.2),
          -1px -1px 1px rgba(248, 249, 253, 0.3);
        border-radius: 10px;
        height: 20px;
        gap: 0;
        margin-right: 2px;
        padding: 2px 3px;
      `}
    >
      {iconsToShow.map((colour, index) => (
        <FlexRowContainer
          key={index}
          css={css`
            padding: 1px;
          `}
        >
          <BookmarkIcon size="small" colour={colour} />
        </FlexRowContainer>
      ))}
      {showPlus && (
        <p
          css={(theme) => ({
            ...theme.customTypography.label._4,
            color: theme.colours.primary.mid.light20,
            fontSize: "10px",
          })}
        >
          +
        </p>
      )}
    </FlexRowContainer>
  );
};

const CountChip = ({
  count,
  showCount,
  variant,
}: {
  count: number;
  showCount: boolean;
  variant: "annotation" | "bookmark";
}) => (
  <FlexRowContainer
    css={({ colours }) => css`
      box-shadow:
        1px 2px 4px rgba(32, 42, 91, 0.2),
        -1px -1px 1px rgba(248, 249, 253, 0.3);
      border-radius: 10px;
      color: ${colours.accent.dark._10};
      gap: 1px;
      margin-right: 2px;
      padding: 2px 3px;
      ${variant === "annotation"
        ? `
          opacity: 1;
          justify-content: flex-start;
          width: fit-content;
          margin-left: 12px;
          background-color: ${colours.highlight.yellow};
        `
        : `
          background-color: ${colours.primary.light._20};
        `}
    `}
  >
    {showCount && (
      <p
        css={(theme) => ({
          ...theme.customTypography.label._4,
        })}
      >
        {count}
      </p>
    )}
    {variant === "annotation" ? (
      <FlexRowContainer
        css={() => ({
          borderRadius: "50%",
          height: "16px",
          width: "16px",
        })}
      >
        <AnnotationIcon sx={{ height: "12px" }} />
      </FlexRowContainer>
    ) : (
      <FlexRowContainer>
        <MUIBookmarkIcon sx={{ height: "14px", width: "14px", padding: "0" }} />
      </FlexRowContainer>
    )}
  </FlexRowContainer>
);

export const CustomTreeItem = memo(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  React.forwardRef<HTMLLIElement, any>(({ annotationsOpen, ...props }, ref) => {
    const {
      itemId,
      children,
      label,
      disabled,
      searchView,
      goToSection,
      searchTerm,
      publicationId,
      loadingItemId,
      ...other
    } = props;

    const {
      getRootProps,
      getContentProps,
      getIconContainerProps,
      getLabelProps,
      status,
      publicAPI,
    } = useTreeItem2({
      itemId,
      label,
      children,
      disabled,
      rootRef: ref,
    });

    const item = publicAPI.getItem(itemId);
    const menuType = item?.menuType;
    const hasChildren = !!item?.children?.length;

    if (menuType === "issue") {
      const isCurrent = itemId === publicationId;
      const isLoading = itemId === loadingItemId;
      return (
        <TreeItem2Provider itemId={itemId}>
          <TreeItem2Root {...getRootProps(other)}>
            <CustomContent
              {...getContentProps({
                className: clsx("chapter", { "Mui-selected": isCurrent }),
              })}
              style={{ paddingLeft: "8px" }}
            >
              <TreeItem2IconContainer {...getIconContainerProps()}>
                {isLoading ? (
                  <CircularProgress
                    size={16}
                    thickness={5}
                    css={css`
                      opacity: 0.6;
                      flex-shrink: 0;
                    `}
                  />
                ) : (
                  <img
                    src={ExpandCircleIcon}
                    alt="Toggle menu"
                    css={css`
                      ${!status.expanded ? "transform: rotate(270deg);" : ""}
                    `}
                  />
                )}
              </TreeItem2IconContainer>
              <div
                css={css`
                  min-width: 0;
                  overflow: hidden;
                `}
              >
                <div
                  css={css`
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  `}
                  title={item?.label}
                >
                  {item?.label}
                </div>
                {item?.author && (
                  <div
                    css={(theme) => css`
                      font-size: 0.7em;
                      font-weight: 400;
                      opacity: 0.75;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                      color: ${theme.colours.accent.light._40};
                    `}
                  >
                    {item.author}
                  </div>
                )}
              </div>
            </CustomContent>
            <Collapse in={status.expanded} timeout="auto" unmountOnExit>
              <FlatChildList>{children}</FlatChildList>
            </Collapse>
          </TreeItem2Root>
        </TreeItem2Provider>
      );
    }

    if (!itemId.startsWith(publicationId)) {
      return null;
    }
    const nestingLevel =
      itemId.replace(`${publicationId}-`, "").split("-").length - 1;
    const annotations = annotationsOpen ? item?.annotations : undefined;
    const bookmarkCollectionColours: string[] =
      item?.bookmarkCollectionColours ?? [];
    const ownBookmarkCollectionColours: string[] =
      item?.ownBookmarkCollectionColours ?? [];
    const tag = item?.type;

    // Determine parent and sibling position for arc icons
    const parentId = itemId.substring(0, itemId.lastIndexOf("-"));
    const parentItem = parentId ? publicAPI.getItem(parentId) : undefined;
    const siblings = parentItem?.children ?? [];
    const siblingIndex = siblings.findIndex(
      (s: { id: string }) => s.id === itemId,
    );
    const isLastSibling = siblingIndex === siblings.length - 1;

    // Determine grandparent info (for children of undefined items)
    const grandparentId = parentId.substring(0, parentId.lastIndexOf("-"));
    const grandparentItem = grandparentId
      ? publicAPI.getItem(grandparentId)
      : undefined;
    const parentSiblings = grandparentItem?.children ?? [];
    const parentSiblingIndex = parentSiblings.findIndex(
      (s: { id: string }) => s.id === parentId,
    );
    const isParentLastSibling =
      parentSiblingIndex === parentSiblings.length - 1;

    const searchResult = item?.searchHit;
    if (
      searchView &&
      searchResult &&
      !menuType &&
      tag !== "h2" &&
      tag !== "h3"
    ) {
      // This is for rendering search results that are a descendant of a leaf or display node as opposed to
      // a section or chapter, so they don't have the same styling as the rest of the tree menu items.
      // They are rendered in a flat list under the nearest section or chapter heading.
      return (
        <TreeItem2Provider itemId={itemId}>
          <div css={{ padding: "4px" }}>
            <SearchResult
              searchResult={searchResult}
              goToSection={goToSection}
              searchTerm={searchTerm}
            />
          </div>
        </TreeItem2Provider>
      );
    }

    return (
      <TreeItem2Provider itemId={itemId}>
        <TreeItem2Root {...getRootProps(other)}>
          <CustomContent
            id={`${searchView ? "search-" : ""}nav-${itemId}`}
            annotations={annotations}
            {...getContentProps({
              className: clsx(
                menuType,
                searchView ? `searchResult nesting-${nestingLevel}` : "",
                {
                  "Mui-expanded": status.expanded,
                  "Mui-selected": status.selected,
                },
              ),
            })}
            style={
              !searchView
                ? {
                    paddingLeft: `${8 + nestingLevel * 32}px`,
                    position: "relative" as const,
                  }
                : undefined
            }
          >
            {/* Annotation indicator bar */}
            {annotations > 0 && (
              <div
                css={(theme) => css`
                  position: absolute;
                  left: 0;
                  top: 0;
                  width: 4px;
                  height: 100%;
                  background-color: ${theme.colours.highlight.yellow};
                  z-index: 11;
                `}
              />
            )}
            {/* Arc icons for tree connector lines */}
            {!searchView &&
              (() => {
                const arcStyle = (leftOffset: number) => css`
                  height: 100%;
                  position: absolute;
                  left: ${leftOffset}px;
                  top: 0;
                  z-index: 10;
                `;
                const baseLeft = 8 + (nestingLevel - 1) * 32 - 12;

                return (
                  <>
                    {/* Sections under a chapter */}
                    {menuType === "section" &&
                      parentItem?.menuType === "chapter" && (
                        <img
                          src={
                            isLastSibling || status.expanded
                              ? arcEndIcon
                              : arcMidIcon
                          }
                          alt=""
                          aria-hidden="true"
                          css={arcStyle(baseLeft)}
                        />
                      )}

                    {/* Undefined items (children of section or chapter) */}
                    {!menuType &&
                      (parentItem?.menuType === "section" ||
                        parentItem?.menuType === "chapter") && (
                        <img
                          src={isLastSibling ? arcEndIcon : arcMidIcon}
                          alt=""
                          aria-hidden="true"
                          css={arcStyle(baseLeft)}
                        />
                      )}

                    {/* Children of undefined items (grandchildren of section or chapter) */}
                    {!menuType &&
                      parentItem &&
                      !parentItem.menuType &&
                      (grandparentItem?.menuType === "section" ||
                        grandparentItem?.menuType === "chapter") && (
                        <>
                          {!isParentLastSibling && (
                            <img
                              src={arcChildIcon}
                              alt=""
                              aria-hidden="true"
                              css={arcStyle(8 + (nestingLevel - 2) * 32 - 12)}
                            />
                          )}
                          <img
                            src={isLastSibling ? arcEndIcon : arcMidIcon}
                            alt=""
                            aria-hidden="true"
                            css={arcStyle(baseLeft - 3)}
                          />
                        </>
                      )}
                  </>
                );
              })()}
            <FlexRowContainer style={{ position: "relative", width: "100%" }}>
              {hasChildren && menuType && (
                <TreeItem2IconContainer {...getIconContainerProps()}>
                  <img
                    src={
                      menuType === "chapter" || menuType === "section"
                        ? ExpandCircleIcon
                        : ExpandCircleIconDark
                    }
                    alt="Toggle menu"
                    css={css`
                      ${!status.expanded ? "transform: rotate(270deg);" : ""}
                    `}
                  />
                </TreeItem2IconContainer>
              )}

              {!menuType && ownBookmarkCollectionColours.length > 0 && (
                <div
                  css={css`
                    display: grid;
                    gap: 1px;
                    grid-template-columns: ${ownBookmarkCollectionColours.length ===
                    1
                      ? "1fr"
                      : "repeat(2, 1fr)"};
                    position: absolute;
                    left: ${ownBookmarkCollectionColours.length === 1
                      ? "-5px"
                      : "-8px"};
                    align-items: center;
                    width: fit-content;
                    ${hasChildren
                      ? `
                      &:hover .bookmark-icons { display: none; }
                      &:hover .expand-icon { display: flex; }
                    `
                      : ""}
                  `}
                >
                  <div
                    className="bookmark-icons"
                    css={css`
                      display: contents;
                    `}
                  >
                    {(() => {
                      const colours = ownBookmarkCollectionColours;
                      const total = colours.length;
                      const showPlus = total > 4;
                      const iconsToShow = showPlus
                        ? colours.slice(0, 3)
                        : colours.slice(0, 4);

                      return (
                        <>
                          {iconsToShow.map((colour, index) => (
                            <div
                              key={index}
                              css={css`
                                ${index === 2 && total === 3
                                  ? "grid-column: span 2;"
                                  : ""}
                                display: flex;
                                justify-content: center;
                              `}
                            >
                              <BookmarkIcon
                                colour={colour}
                                size={total === 1 ? "medium" : "small"}
                              />
                            </div>
                          ))}
                          {showPlus && (
                            <div
                              css={css`
                                align-items: center;
                                color: #283472;
                                display: flex;
                                font-size: 14px;
                                font-style: normal;
                                height: 0px;
                                justify-content: center;
                                width: 12px;
                              `}
                            >
                              +
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {hasChildren && (
                    <div
                      className="expand-icon"
                      css={css`
                        align-items: center;
                        display: none;
                        justify-content: center;
                        margin-left: ${ownBookmarkCollectionColours.length === 1
                          ? "2px"
                          : "5px"};
                      `}
                    >
                      <img
                        src={ExpandCircleIconDark}
                        alt="Toggle menu"
                        css={css`
                          ${!status.expanded
                            ? "transform: rotate(270deg);"
                            : ""}
                        `}
                      />
                    </div>
                  )}
                </div>
              )}

              {!menuType &&
                ownBookmarkCollectionColours.length === 0 &&
                hasChildren && (
                  <TreeItem2IconContainer
                    {...getIconContainerProps()}
                    css={css`
                      position: absolute;
                      left: -3px;
                    `}
                  >
                    <img
                      src={ExpandCircleIconDark}
                      alt="Toggle menu"
                      css={css`
                        ${!status.expanded ? "transform: rotate(270deg);" : ""}
                      `}
                    />
                  </TreeItem2IconContainer>
                )}

              {!menuType &&
                ownBookmarkCollectionColours.length === 0 &&
                !hasChildren && (
                  <img
                    src={noLowerLevelIcon}
                    alt=""
                    aria-hidden="true"
                    css={css`
                      position: absolute;
                      left: -8px;
                      width: 24px;
                      height: 24px;
                    `}
                  />
                )}

              {menuType && menuType !== "chapter" && menuType !== "section" && (
                <FlexRowContainer
                  css={css`
                    align-items: center;
                    display: flex;
                    height: 100%;
                    gap: 0;
                  `}
                >
                  {bookmarkCollectionColours.length > 0 &&
                    (() => {
                      const colours = bookmarkCollectionColours;
                      const total = colours.length;
                      const showPlus = total > 5;
                      const iconsToShow = showPlus
                        ? colours.slice(0, 5)
                        : colours.slice(0, 6);

                      return (
                        <>
                          {iconsToShow.map((colour, index) => (
                            <div key={index} style={{ display: "flex" }}>
                              <BookmarkIcon size="small" colour={colour} />
                            </div>
                          ))}
                          {showPlus && (
                            <div
                              css={css`
                                align-items: center;
                                color: #283472;
                                display: flex;
                                font-size: 11px;
                                height: 12px;
                                justify-content: center;
                                width: 9px;
                              `}
                            >
                              +
                            </div>
                          )}
                        </>
                      );
                    })()}
                </FlexRowContainer>
              )}

              <TreeItem2Label
                {...getLabelProps()}
                css={css`
                  font-family: inherit;
                  font-weight: inherit;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  ${!menuType ? "margin-left: 20px;" : ""}
                `}
                title={item.label}
              />

              {annotations > 0 && (
                <CountChip
                  count={annotations}
                  showCount={menuType === "chapter"}
                  variant="annotation"
                />
              )}

              {ownBookmarkCollectionColours.length > 0 &&
                menuType === "section" && (
                  <SectionBookmarkChip colours={ownBookmarkCollectionColours} />
                )}

              {bookmarkCollectionColours.length > 0 &&
                (menuType === "chapter" || (!menuType && hasChildren)) && (
                  <CountChip
                    count={bookmarkCollectionColours.length}
                    showCount
                    variant="bookmark"
                  />
                )}
            </FlexRowContainer>
          </CustomContent>
          {searchView && searchResult && (
            <div css={{ padding: "4px" }}>
              <SearchResult
                searchResult={searchResult}
                goToSection={goToSection}
                searchTerm={searchTerm}
              />
            </div>
          )}
          <Collapse in={status.expanded} timeout="auto" unmountOnExit>
            {searchView &&
            (menuType === "subsection" || tag === "h2" || tag === "h3") ? (
              <SubMenuList padMulti>{children}</SubMenuList>
            ) : (
              <FlatChildList>{children}</FlatChildList>
            )}
          </Collapse>
        </TreeItem2Root>
      </TreeItem2Provider>
    );
  }),
);
